import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, X } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServiceModal = ({ isOpen, onClose, service, categories, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    price: '',
    images: [],
    active: true
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name || '',
        description: service.description || '',
        category_id: service.category_id || '',
        price: service.price || '',
        images: service.images || [],
        active: service.active !== undefined ? service.active : true
      });
    } else {
      setFormData({
        name: '',
        description: '',
        category_id: categories[0]?.id || '',
        price: '',
        images: [],
        active: true
      });
    }
  }, [service, categories, isOpen]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/services/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, response.data.url]
      }));
    } catch (error) {
      alert('Hiba történt a kép feltöltése során');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      price: parseInt(formData.price, 10)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 overflow-y-auto">
      <div className="glass rounded-2xl max-w-2xl w-full my-8">
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-2xl font-semibold">
            {service ? 'Szolgáltatás szerkesztése' : 'Új szolgáltatás'}
          </h2>
          <button onClick={onClose} className="text-2xl hover:text-yellow-200">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Név *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                placeholder="Numerológiai elemzés"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Kategória *</label>
              <select
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                required
                className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              >
                <option value="">Válassz kategóriát</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Leírás *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={4}
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white resize-none"
              placeholder="Részletes leírás a szolgáltatásról..."
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Ár (HUF) *</label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              required
              min="0"
              step="1"
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              placeholder="15000"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Képek</label>
            
            {/* Uploaded Images */}
            {formData.images.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-3">
                {formData.images.map((img, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${img}`}
                      alt={`Service ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Button */}
            <label className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg cursor-pointer transition">
              <Upload className="w-5 h-5" />
              <span>{uploading ? 'Feltöltés...' : 'Kép feltöltése'}</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-300">Aktív (látható a felhasználóknak)</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 rounded-lg transition"
            >
              Mégse
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-yellow-200 text-black font-semibold rounded-lg hover:bg-yellow-300 transition"
            >
              Mentés
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.08), hsl(0 0% 100% / 0.04));
          border: 1px solid hsl(0 0% 100% / 0.1);
          backdrop-filter: blur(12px);
        }
      `}</style>
    </div>
  );
};

export default ServiceModal;
